// ---------------------------------------------------------------------------
// What the frame's navigation shows, as data (R12d)
//
// The SideNav and the MobileNav get the same items, the same current page and
// the same brand; this module says what they are, and imports no element, so
// tests/nav-model.test.ts holds it in Node.
//
//   - Items: the Overview (today's dashboard) and Settings, the pages the app
//     has (decision 7.3: new views later, one by one). "New campaign" is no
//     item (d4): it is the Overview's header button and the phone bar's "+".
//   - The current page (d3): the Overview on /, Settings on /settings, and
//     none on a new, edited or opened campaign, as today — marking the
//     Overview aria-current="page" there would tell a screen reader the
//     wrong page.
//   - The phone bar's "+" shows on every framed page but the new campaign
//     itself (e4): the wizard keeps /campaign/new after its first autosave
//     has created the draft, and a navigation to the URL shown does nothing
//     (B5), so a "+" there would do nothing over a saved draft.
//   - The brand: "SDR Agent", endorsed "by NDI", with the NDI mark from
//     static/ (1081 × 421 px), drawn as a mask by the ports' logoMark(), so no
//     url() reaches the stylesheet.
//   - The theme's four options in words (I9: the system has no theme glyphs).
// ---------------------------------------------------------------------------

import type { NavItem } from '../design/nav.js';
import { THEME_CHOICES, type ThemeChoice } from '../theme-choice.js';

/** The ids are the routes' names: a choice goes to the route of that name. */
export type NavTarget = 'dashboard' | 'settings';

export const NAV_ITEMS: readonly (NavItem & { id: NavTarget })[] = [
  { id: 'dashboard', label: 'Overview', icon: 'dashboard' },
  { id: 'settings', label: 'Settings', icon: 'settings' },
];

const CURRENT: Readonly<Record<string, NavTarget>> = { dashboard: 'dashboard', settings: 'settings' };

/** The nav item that is the current page on a route, or undefined (d3). */
export function currentNavItem(route: string): NavTarget | undefined {
  return Object.hasOwn(CURRENT, route) ? CURRENT[route] : undefined;
}

/** Whether the phone bar shows "+" (New campaign) on a route (e4). */
export function showsNewCampaign(route: string): boolean {
  return route !== 'newCampaign';
}

export const BRAND = {
  product: 'SDR Agent',
  endorsement: 'by NDI',
  endorsementMark: { src: '/ndi-mark.png', label: 'NDI', ratio: 2.5677 },
} as const;

/** The theme Select's options in the nav's footer, in words (R12e). */
export const THEME_OPTIONS: readonly { value: ThemeChoice; label: string }[] = THEME_CHOICES.map(
  ({ value, label }) => ({ value, label }),
);
